import * as Print from 'expo-print'
import * as Sentry from '@sentry/react-native'
import i18n from 'i18next'

// expo-print (iOS) rejects the printAsync promise from the print interaction
// completion handler with one of two exceptions:
//   - PrintIncompleteException — message "Printing did not complete" — fired when
//     the user dismisses the AirPrint dialog without printing (completed === false,
//     no error). This is a benign user cancellation, NOT a failure.
//   - PrintingJobFailedException — message "Printing job encountered an error: ..."
//     or "Provided printer is not available." — a real failure worth surfacing.
//
// Historically these print calls were fire-and-forget (or caught only to
// captureException), so every benign cancel flooded Sentry (QARASAY-KASSA-1N5,
// 1171 events) and tripped the global unhandledrejection handler. This wrapper
// catches the rejection, classifies it, and NEVER rejects: silent on cancel,
// friendly toast + Sentry on a genuine failure.

const isUserCancellation = (error) => {
  const message = (error && (error.message || String(error))) || ''
  return message.includes('Printing did not complete')
}

/**
 * Print HTML/URI via expo-print without ever letting the promise reject.
 *
 * @param {import('expo-print').PrintOptions} options - expo-print options (html or uri).
 * @param {Object} [deps] - Optional UI dependencies for user feedback on failure.
 * @param {{ show: Function }} [deps.toast] - react-native-toast-notifications instance.
 * @param {Function} [deps.t] - i18next translate fn (falls back to global i18n).
 * @returns {Promise<boolean>} true if printing started, false on cancel/failure.
 */
export async function safePrintAsync(options, { toast, t } = {}) {
  try {
    await Print.printAsync(options)
    return true
  } catch (error) {
    if (isUserCancellation(error)) {
      // User dismissed the print dialog — nothing went wrong, stay silent.
      return false
    }
    // Genuine print failure (printer unreachable / job error).
    Sentry.captureException(error)
    const translate = t || i18n.t.bind(i18n)
    if (toast) {
      toast.show(translate('printFailed'), {
        type: 'danger',
        placement: 'bottom',
        duration: 3000,
      })
    }
    return false
  }
}
